/**
 * Licence — the current licence Revia signed for one managed client's
 * Firebase project, at `workspaces/revia/licences/{projectId}`: Revia's whole
 * record of a managed client. Which clients a release reaches, and where, is
 * the client's GitHub environment in ReVia-Workspace/Revent (its
 * `LICENCE_TOKEN` secret carries this record's token); Revia keeps only what
 * it signed. One per project, so re-signing replaces it. The payload and
 * token shapes are Revent's (`Licence` in `@momo/types`); `signLicence`
 * (`signLicence.action.ts`) writes the record.
 *
 * The doc id is the client's project id, the licence's authoritative
 * identity: the signed payload binds the token to that one project.
 *
 * Fields justified:
 *   v, projectId, features, endsAt, graceDays — the signed payload, as
 *                  signed; `projectId` is the doc id. The list reads the
 *                  expiry from `endsAt` and the grace length from
 *                  `graceDays`.
 *   client       — the managed client's display name; the list's first
 *                  column.
 *   environment  — the client's GitHub environment in ReVia-Workspace/Revent,
 *                  `client-<slug>` derived from the client's name at the first
 *                  signing and kept on every re-signing, so a renamed client
 *                  still renews into the environment its deploys read.
 *   token        — the compact signed token, which staff copy into the
 *                  client's environment as `LICENCE_TOKEN`.
 *   signedAt     — when Revia signed it; the list's order.
 *   signedBy     — the signing staff member's uid, the attribution the staff
 *                  notice promises.
 *
 * Excluded:
 *   status       — active, grace or read-only follow from `endsAt` and
 *                  `graceDays` at read time.
 *   keyVersion   — nothing reads it; `signLicence` logs the version it
 *                  signed with.
 *   hostname / licenceRequired / project variables — the deploy's facts,
 *                  held by the client's GitHub environment; a client needing
 *                  no licence has no record here.
 *   history      — earlier licences for the project; nothing reads them.
 *
 * Access: read and list for Revia staff signed in with a second factor (the
 * `mfa` claim), as Revent's permission-gated member reads are. No client
 * write: the records are written by Revia's server.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { Licence as SignedLicence, Workspace } from "@momo/types";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { REVIA_ORG_ID, STAFF_PERMISSION } from "./staff.const.ts";

// A Google Cloud project id as a person types it, normalized before the
// constraint: 6–30 characters, lowercase letters, digits and hyphens,
// starting with a letter and not ending with a hyphen.
export const ProjectId = z
	.string()
	.trim()
	.toLowerCase()
	.regex(/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/)
	.describe("the managed client's Google Cloud project id");
export type ProjectId = z.infer<typeof ProjectId>;

// A client's GitHub environment: `client-` and a slug of lowercase letters
// and digits in hyphen-separated runs.
export const EnvironmentName = z
	.string()
	.regex(/^client-[a-z0-9]+(-[a-z0-9]+)*$/)
	.max(60)
	.describe("the managed client's GitHub environment name");
export type EnvironmentName = z.infer<typeof EnvironmentName>;

/** The GitHub environment a client's name gives: `client-` and its name in
 *  lowercase ASCII, accents dropped and every other run of characters one
 *  hyphen. Null for a name that leaves no letter or digit. */
export function clientEnvironment(client: string): EnvironmentName | null {
	const slug = client
		.normalize("NFKD")
		.replace(/\p{M}+/gu, "")
		.toLowerCase()
		.replace(/ß/g, "ss")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 52)
		.replace(/-+$/, "");
	return slug === "" ? null : `client-${slug}`;
}

export const LicenceSchema = SignedLicence.LicencePayloadSchema.extend({
	client: z.string().trim().min(1).describe("the managed client's display name"),
	environment: EnvironmentName,
	token: SignedLicence.PlatformLicenceSchema.shape.token,
	signedAt: z.custom<Timestamp>((value) => value != null),
	signedBy: Workspace.Id.describe("the signing staff member's uid"),
}).strict();
export type Licence = z.infer<typeof LicenceSchema>;

const staff = { workspace: REVIA_ORG_ID };

export const Licence = entity<Workspace.Actor>()({
	schema: LicenceSchema,
	actions: ["read", "list"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, STAFF_PERMISSION, staff),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, STAFF_PERMISSION, staff),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: `workspaces/${REVIA_ORG_ID}/licences/{projectId}`,
		}),
	);
